import type { ParsedStructuredElementType } from '../layout/types';

import {
	convertActionCard,
	convertCarousel,
	convertContactCard,
	convertJiraBurndownChart,
	convertJiraEpicProgress,
	convertJiraWorkDistribution,
	convertPeopleCard,
	convertProjectHighlightCard,
	convertReportLines,
} from './converters/card-converters';
import { convertList } from './converters/list-converter';
import { convertPeopleEntity, convertPeopleEntityList } from './converters/people-entity-converter';
import { convertSmartLink } from './converters/smartlink-converter';
import { convertSmartLinkList } from './converters/smartlinklist-converter';
import { convertH, convertMd } from './converters/text-converters';
import type { AdfContentNode, RecursiveTagConverter, TagConverter } from './types';

/**
 * Fallback converter for unrecognized Lumina tags.
 * convertMd is used because it maps to a paragraph node in ADF.
 */
const fallbackConverter: TagConverter = convertMd;

/**
 * A no-op converter that returns undefined.
 * Used for tags that are handled elsewhere or should be skipped.
 */
const skipTag: TagConverter = () => undefined;

const TAG_CONVERTERS: Record<string, TagConverter | RecursiveTagConverter> = {
	h: convertH,
	md: convertMd,
	carousel: convertCarousel,
	'smart-link': convertSmartLink,
	'smart-link-list': convertSmartLinkList,
	'action-card': convertActionCard,
	'project-highlight-card': convertProjectHighlightCard,
	'contact-card': convertContactCard,
	'people-card': convertPeopleCard,
	'report-lines': convertReportLines,
	ul: convertList,
	ol: convertList,
	list: convertList,
	'jira-epic-progress': convertJiraEpicProgress,
	'jira-burndown-chart': convertJiraBurndownChart,
	'jira-work-distribution': convertJiraWorkDistribution,
	'people-entity': convertPeopleEntity,
	'people-entity-list': convertPeopleEntityList,
	// Sections should be handled at the layout level by convertSection.
	// This no-op is only for the unexpected case of a section reaching this converter.
	section: skipTag,
};

/**
 * Converts a single parsed Lumina node to an ADF node.
 * Tag converters return a full ADF DocNode content node.
 * Converters will handle their own fallback logic; unrecognized tags fall back to paragraph.
 */
export const luminaToAdfJsonNode = (
	luminaNode: ParsedStructuredElementType,
): AdfContentNode | undefined => {
	const converter = TAG_CONVERTERS[luminaNode.tag];

	// If there's a registered converter, use its result (even if undefined)
	if (converter) {
		return converter(luminaNode, luminaToAdfJsonNode);
	}

	// For unregistered tags, fall back to paragraph
	return fallbackConverter(luminaNode);
};
